/**
 * Post-auth redirect handling.
 *
 * Two layers, because Clerk's email-verification and OAuth flows do full page
 * navigations — the OAuth callback lands on `/sign-up/sso-callback` with no
 * query string, so a `?redirect_url=` param alone cannot carry the destination
 * across:
 *
 *  1. `?redirect_url=` on the sign-in / sign-up URLs (survives same-SPA hops).
 *  2. A short-lived `sessionStorage` bridge (survives full reloads).
 */

const BRIDGE_KEY = 'dizcant:auth-redirect'
const BRIDGE_TTL_MS = 10 * 60 * 1000

const AUTH_PREFIXES = ['/sign-in', '/sign-up', '/welcome']

/**
 * Returns `path` only if it is a safe same-origin in-app path, else `'/'`.
 *
 * Rejects protocol-relative and backslash-normalised URLs (`//evil.com`,
 * `/\evil.com` — browsers treat `\` as `/` in the authority), absolute URLs,
 * and any auth route (which would loop).
 */
export function safeRedirect(value: string | null | undefined): string {
  if (!value || !/^\/[^/\\]/.test(value)) {
    return '/'
  }
  if (AUTH_PREFIXES.some((prefix) => value.startsWith(prefix))) {
    return '/'
  }
  return value
}

interface Bridge {
  path: string
  savedAt: number
}

/** Remember where to return after auth. Safe to call with an unsafe value. */
export function rememberAuthRedirect(path: string): void {
  const safe = safeRedirect(path)
  if (safe === '/') {
    return
  }
  try {
    const bridge: Bridge = { path: safe, savedAt: Date.now() }
    sessionStorage.setItem(BRIDGE_KEY, JSON.stringify(bridge))
  } catch {
    // Storage unavailable (Safari private mode) — degrade to the query param.
  }
}

function readBridge(): string | null {
  try {
    const raw = sessionStorage.getItem(BRIDGE_KEY)
    if (!raw) {
      return null
    }
    const bridge = JSON.parse(raw) as Partial<Bridge>
    if (
      typeof bridge.path !== 'string' ||
      typeof bridge.savedAt !== 'number' ||
      Date.now() - bridge.savedAt > BRIDGE_TTL_MS
    ) {
      sessionStorage.removeItem(BRIDGE_KEY)
      return null
    }
    return safeRedirect(bridge.path)
  } catch {
    return null
  }
}

/** Read the remembered destination without consuming it. */
export function peekAuthRedirect(): string | null {
  const path = readBridge()
  return path && path !== '/' ? path : null
}

/** Read the remembered destination and clear it. Defaults to `'/'`. */
export function takeAuthRedirect(): string {
  const path = readBridge()
  try {
    sessionStorage.removeItem(BRIDGE_KEY)
  } catch {
    // ignore
  }
  return path ?? '/'
}

/** Append `?redirect_url=` to an auth path, unless the destination is `'/'`. */
export function withRedirect(path: string, redirect: string): string {
  const safe = safeRedirect(redirect)
  if (safe === '/') {
    return path
  }
  return `${path}?redirect_url=${encodeURIComponent(safe)}`
}
